import React, { useState, useEffect } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Button,
  Card,
  Divider,
  Paper,
  Grid,
  Typography,
  TextField,
  Box,
  CircularProgress,
  useTheme,
  TablePagination,
  IconButton,
  Modal,
  Avatar,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonIcon from "@mui/icons-material/Person";
import Timeline from "@mui/icons-material/Timeline";
import Numbers from "@mui/icons-material/Numbers";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DownloadIcon from "@mui/icons-material/Download"; // <-- Add this import
import CreateIcon from "@mui/icons-material/Create";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import api from "../../../utils/Url";
import moment from "moment";


interface FileDocument {
  fileID: number;
  fileName: string;
  fileNo: string;
  fileType: number;
  fileGroup: number;
  fileCategory: number;
  fileClass: number;
  fileSubject: number;
  synopsis: string;
  remark: string;
  refID: number;
  totalPages: number;
  fromPage: number;
  toPage: number;
  status: string;
  originLocation: string;
  currentLocation: string;
  zoneID: number;
  wardID: number;
  createdDate: string;
  createdBy: string;
  updatedBy: string;
  updatedDate: string;
  rfid: string;
  senderID: string;
  receiverID: string;
  fileDoc: string; // This will contain the base64 encoded file
}



interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pdfBlob: Blob | null;
  onDownload: () => void;
}



// Custom pagination actions component
function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.875rem",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
const TransferHistoryModal = ({ open, onClose, transferData }: { 
  open: boolean, 
  onClose: () => void, 
  transferData: any 
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="transfer-history-modal"
      aria-describedby="transfer-history-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '60vw', xl: '50vw' },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header with gradient background */}
      <Box sx={{
              background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
              color: 'white',
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t("text.FileMovementDetails")}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t("text.FileName")}: {transferData?.fileName || t("text.NA")} | {t("text.FileNumber")}: {transferData?.fileNumber || t("text.NA")}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white', 
              '&:hover': { 
                backgroundColor: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'transform 0.2s'
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        
        {/* Main content with scrollable area */}
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          <Grid container spacing={3}>
            {/* Transfer Details Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                height: '100%',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: 'primary.main', 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <SwapHorizIcon color="primary" /> {t("text.TransferDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem 
                  label={t("text.FileNumber")} 
                  icon={<Numbers fontSize="small" />}
                  value={transferData?.fileNumber} 
                />
                <DetailItem 
                  label={t("text.Location")} 
                  value={transferData?.fromLocation} 
                  icon={<LocationOnIcon fontSize="small" />}
                />
                {/* <DetailItem 
                  label={t("text.ToLocation")} 
                  value={transferData?.toLocation || t("text.NotSpecified")} 
                  icon={<LocationOnIcon fontSize="small" />}
                /> */}
                <DetailItem 
                  label={t("text.Through")} 
                  value={transferData?.throughBy || t("text.DirectTransfer")} 
                  icon={<AccountTreeIcon fontSize="small" />}
                />
              </Paper>
            </Grid>
            
            {/* Personnel Section */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                height: '100%',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: 'primary.main', 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <PeopleAltIcon color="primary" /> {t("text.Personnel")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <DetailItem 
                  label={t("text.Sender")} 
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: stringToColor(transferData?.senderName || '') 
                      }}>
                        {transferData?.senderName?.charAt(0) || '?'}
                      </Avatar>
                      {transferData?.senderName || t("text.NA")}
                    </Box>
                  } 
                />
                <DetailItem 
                  label={t("text.Receiver")} 
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: stringToColor(transferData?.receiverName || '') 
                      }}>
                        {transferData?.receiverName?.charAt(0) || '?'}
                      </Avatar>
                      {transferData?.receiverName || t("text.NA")}
                    </Box>
                  } 
                />
                <DetailItem 
                  label={t("text.InitiatedBy")} 
                  value={transferData?.createdBy || t("text.System")} 
                  icon={<PersonIcon fontSize="small" />}
                />
              </Paper>
            </Grid>
            
            {/* Timeline Section */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                border: '1px solid #e0e0e0',
                background: 'linear-gradient(to bottom right, #f5f5f5, #ffffff)'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: 'primary.main', 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Timeline color="primary" /> {t("text.Timeline")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 3,
                  justifyContent: 'space-between'
                }}>
                  <TimelineItem 
                    icon={<EventAvailableIcon color="primary" />}
                    title={t("text.FileDate")}
                    date={moment.utc(transferData?.fileDate).format('DD/MM/YYYY') || t("text.NA")}
                  />
                  <TimelineItem 
                    icon={<CreateIcon color="primary" />}
                    title={t("text.CreatedDate")}
                    date={moment.utc(transferData?.createdDate).format('DD/MM/YYYY') || t("text.NA")}
                  />
                  <TimelineItem 
                    icon={<DownloadIcon color="primary" />}
                    title={t("text.ReceivedDate")}
                    date={moment.utc(transferData?.receivedDate).format('DD/MM/YYYY') || t("text.NA")}
                  />
                  <TimelineItem 
                    icon={<SendIcon color={transferData?.transferDate ? "primary" : "disabled"} />}
                    title={t("text.TransferDate")}
                    date={transferData?.transferDate ? moment.utc(transferData.transferDate).format('DD/MM/YYYY') : t("text.NotTransferredYet")}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        {/* Footer with action buttons */}
     
      </Box>
    </Modal>
  );
};

// Helper component for timeline items
const TimelineItem = ({ icon, title, date }: { icon: React.ReactNode, title: string, date: string }) => {
  const { t } = useTranslation();
  
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.5,
      flex: 1,
      minWidth: { sm: 0, md: '180px' }
    }}>
      <Box sx={{ color: 'primary.main' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {date}
        </Typography>
      </Box>
    </Box>
  );
};

// Helper component for consistent detail items
const DetailItem = ({ 
  label, 
  value, 
  icon 
}: { 
  label: string; 
  value?: React.ReactNode; 
  icon?: React.ReactNode 
}) => {
  const { t } = useTranslation();
  
  return (
    <Box sx={{ 
      mb: 2,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5
    }}>
      {icon && (
        <Box sx={{ 
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '2px'
        }}>
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {label}
        </Typography>
        {value ? (
          typeof value === 'string' || typeof value === 'number' ? (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {value || t("text.NA")}
            </Typography>
          ) : (
            value
          )
        ) : (
          <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            {t("text.NA")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Helper function to generate color from string (for avatars)
const stringToColor = (string: string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

// Helper function to format dates
const formatDate = (dateString?: string | null) => {
 // const { t } = useTranslation();
  if (!dateString) return "NA";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};


function Row({ row, index }: { row: any; index: number }) {
  const [modalOpen, setModalOpen] = useState(false);
     const { t } = useTranslation();
const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileDocument, setFileDocument] = useState<FileDocument | null>(null);
  
   const fetchFileDocument = async (fileID: number) => {
      setFileLoading(true);
      try {
        const response = await api.post('FileDetail/GetFileDetails', {
          ZoneID: -1,
          wardID: -1,
          fileID: fileID,
          fileType: -1,
          fileGroup: -1,
          fileCategory: -1,
          fileClass: -1,
          fileSubject: -1
        });
        
        if (response.data.isSuccess && response.data.data && response.data.data.length > 0) {
          setFileDocument(response.data.data[0]);
          setFileModalOpen(true);
        }
      } catch (err) {
        console.error("Error fetching file document:", err);
      } finally {
        setFileLoading(false);
      }
    };
  
    const renderFileContent = () => {
      if (!fileDocument || !fileDocument.fileDoc) {
        return <Typography>{t("text.NoFileContentAvailable")}</Typography>;
      }
  
      const [mimeType, base64Data] = fileDocument.fileDoc.split(';base64,');
      
      if (mimeType.includes('application/pdf')) {
        return (
          <embed 
            src={fileDocument.fileDoc} 
            type="application/pdf" 
            width="100%" 
            height="100%"
            style={{ minHeight: '500px' }}
          />
        );
      } else if (mimeType.includes('image/')) {
        return (
          <img 
            src={fileDocument.fileDoc} 
            alt={fileDocument.fileName}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        );
      } else {
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">{fileDocument.fileName}</Typography>
            <Typography>{t("text.UnsupportedFileFormat")}</Typography>
            <Button 
              variant="contained" 
              href={fileDocument.fileDoc} 
              download={fileDocument.fileName}
              sx={{ mt: 2 }}
            >
              {t("text.DownloadFile")}
            </Button>
          </Box>
        );
      }
    };
  
  
  return (
    <>
      <StyledTableRow>
        <StyledTableCell align="center">{index + 1}</StyledTableCell>
        <StyledTableCell align="center">
          <Typography 
            onClick={() => setModalOpen(true)}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            {row.fileName || "NA"}
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="center">{row.fromLocation || "NA"}</StyledTableCell>
        {/* <StyledTableCell align="center">{row.toLocation || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{row.refId || "NA"}</StyledTableCell> */}
        <StyledTableCell align="center">{row.senderName || "NA"}</StyledTableCell>
        <StyledTableCell align="center">{row.receiverName || "NA"}</StyledTableCell>
    
     {/* Add the preview button cell */}
            <StyledTableCell align="center">
              <IconButton
                onClick={() => fetchFileDocument(row.fileID)}
                color="primary"
                size="small"
                disabled={fileLoading}
                sx={{ '&:hover': { backgroundColor: 'transparent' } }}
              >
                {fileLoading ? <CircularProgress size={24} /> : <VisibilityIcon />}
              </IconButton>
            </StyledTableCell>
      </StyledTableRow>

      <TransferHistoryModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        transferData={row}
      />

       {/* File Preview Modal */}
                  <Modal
                    open={fileModalOpen}
                    onClose={() => setFileModalOpen(false)}
                    aria-labelledby="file-preview-modal"
                    aria-describedby="file-preview"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(2px)',
                    }}
                  >
                    <Box sx={{
                      width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
                      height: { xs: '85vh', sm: '80vh', md: '75vh' },
                      bgcolor: 'background.paper',
                      boxShadow: 24,
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      border: '4px solid transparent',
                      backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'content-box, border-box',
                      position: 'relative',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        zIndex: -1,
                        margin: '-4px',
                        borderRadius: 'inherit',
                        background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                      }
                    }}>
                      {/* Header with gradient background */}
                      <Box sx={{
                        background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
                        color: 'white',
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t("text.PreviewFile")} - {fileDocument?.fileName}
                        </Typography>
                        <IconButton 
                          onClick={() => setFileModalOpen(false)} 
                          sx={{ 
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.1)'
                            }
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      
                      {/* File Content */}
                      <Box sx={{ 
                        flex: 1, 
                        overflow: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: '#f5f5f5',
                        p: 2
                      }}>
                        {renderFileContent()}
                      </Box>
                    </Box>
                  </Modal>
    </>
  );
}

const PDFPreviewModal = ({ open, onClose, pdfBlob, onDownload }: PDFPreviewModalProps) => {
  const { t } = useTranslation();
  const [objectURL, setObjectURL] = useState<string | null>(null);

  useEffect(() => {
    if (pdfBlob && open) {
      const url = URL.createObjectURL(pdfBlob);
      setObjectURL(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [pdfBlob, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="pdf-preview-modal"
      aria-describedby="pdf-preview"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box sx={{
        width: { xs: '95vw', sm: '85vw', md: '75vw', lg: '65vw' },
        height: { xs: '85vh', sm: '80vh', md: '75vh' },
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '4px solid transparent',
        backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'content-box, border-box',
        position: 'relative',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: -1,
          margin: '-4px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
        }
      }}>
        {/* Header with gradient background */}
        <Box sx={{
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("text.PDFPreview")}
          </Typography>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* PDF Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#f5f5f5'
        }}>
          {objectURL ? (
            <iframe 
              src={`${objectURL}#toolbar=0&navpanes=0`}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
              title="PDF Preview"
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 2
            }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body1" color="textSecondary">
                {t("text.LoadingPDF")}...
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Footer with actions */}
        <Box sx={{
          p: 2,
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            variant="outlined" 
            onClick={onClose}
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none'
            }}
          >
            {t("text.Cancel")}
          </Button>
          <Button 
            variant="contained" 
            onClick={onDownload}
            sx={{ 
              minWidth: 120,
              fontWeight: 600,
              borderRadius: 1,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #303f9f 0%, #1976d2 100%)',
              }
            }}
          >
            {t("text.Download")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default function FileMovementReports() {
  const { t } = useTranslation();
  const theme = useTheme();
const [pdfModalOpen, setPdfModalOpen] = useState(false);
const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
const [pdfDownloadFn, setPdfDownloadFn] = useState<(() => void) | null>(null);
  const [data, setData] = useState<any>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [optionLocation, setOptionLocation] = useState([
    { value: "-1", label: t("text.SelectFromLocation") },
  ]);
  const [optionMember, setOptionMember] = useState([
    { value: "-1", label: t("text.SelectSenderMember") },
  ]);
  const [optionFileDetailsAll, setOptionFileDetailsAll] = useState([
    { value: "-1", label: t("text.SelectFile") },
  ]);
  const [optionRefid, setOptionRefid] = useState([
    { label: t("text.SelectRFID") },
  ]);
  const [optionDepartment, setOptionDepartment] = useState([
    { value: "-1", label: t("text.SelectDepartment") },
  ]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getLocationData();
    getFileTypeData();
    getFileDtailsAllData();
    getDepartmentData();
  }, []);

  const getDepartmentData = () => {
    const collectData = {
      departmentId: -1,
    };

    api.post("Department/GetDepartmentmaster", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["departmentName"],
          value: res.data.data[index]["departmentId"],
        });
      }
      setOptionDepartment(arr);
    });
  };

  const getLocationData = () => {
    const collectData = {
      id: -1,
    };

    api.post("LocationItems/GetMappedLocation", collectData).then((res) => {
      const arr = [];
      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["location"],
          value: res.data.data[index]["id"],
        });
      }
      setOptionLocation(arr);
    });
  };

  const getFileTypeData = () => {
    const collectData = {
      useR_ID: "-1",
    };
    api.post(`USER/GetUSER`, collectData).then((res) => {
      const arr = res.data.data.map((item: any) => ({
        label: item.firsT_NAME + " " + item.middlE_NAME + "  " + item.suR_NAME,
        value: item.useR_ID,
      }));
      setOptionMember(arr);
    });
  };

  const getFileDtailsAllData = () => {
    const collectData = {"fileID":-1,"chackNo":"","fileType":-1,"fileGroup":-1,"fileCategory":-1,"fileClass":-1,"fileSubject":-1,"zoneID":-1,"wardID":-1,"rfid":"","user_ID":""};

    api.post("FileDetail/GetFileDetails", collectData).then((res) => {
      const arr = [];
      const arr1: any = [];

      for (let index = 0; index < res.data.data.length; index++) {
        arr.push({
          label: res.data.data[index]["fileNo"] + " - " + res.data.data[index]["fileName"],
          value: res.data.data[index]["fileID"],
        });
      }

      for (let index = 0; index < res.data.data.length; index++) {
        const rfid = res.data.data[index]["rfid"];
        const refID = res.data.data[index]["fileID"];

        if (rfid !== "" && rfid !== 0) {
          
          arr1.push({
            label: rfid,
            value: refID,
          });
        }
      }
      setOptionRefid(arr1);
      setOptionFileDetailsAll(arr);
    });
  };

  interface FormValues {
    fileID: any;
    fileNumber: any;
    fileName: any;
    hid: any;
    fromLocation: any;
    toLocation: any;
    refId: any;
    sender: any;
    receiver: any;
  }

  const initialValues: FormValues = {
    hid: -1,
    fileID: 0,
    fileNumber: "",
    fileName: "",
    fromLocation: "",
    toLocation: "",
    refId: "",
    sender: "",
    receiver: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setHasSearched(true);
        setPage(0); // Reset to first page when new search is performed

        if (values.fileID === initialValues.fileID) {
          values.fileID = -1;
        }
        if (values.fileNumber === initialValues.fileNumber) {
          values.fileNumber = "";
        }
        if (values.fromLocation === initialValues.fromLocation) {
          values.fromLocation = "";
        }
        if (values.toLocation === initialValues.toLocation) {
          values.toLocation = "";
        }
        if (values.refId === initialValues.refId) {
          values.refId = "";
        }
        if (values.sender === initialValues.sender) {
          values.sender = "";
        }
        if (values.receiver === initialValues.receiver) {
          values.receiver = "";
        }

        const response = await api.post(
          `FileMovementHistory/GetFileMovementHistoryFilter`,
          values
        );
        setData(response.data.data);
        if (response.data.isSuccess) {
          // toast.success(response.data.mesg);
        } else {
          // toast.error(response.data.mesg);
        }
      } catch (error) {
        toast.error("An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    },
  });

const exportToPDF = () => {
  if (data.length === 0) {
    toast.warning(t("text.NoDataToExport"));
    return;
  }

  // Create a new PDF document in landscape
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  // Add title and metadata
  doc.setProperties({
    title: "File Movement Report",
    subject: "File Transfer History",
    author: "Your Application",
  });

  // Add header with title
  doc.setFontSize(20);
  doc.setTextColor(63, 81, 181);
  doc.setFont("helvetica", "bold");
  doc.text("FILE MOVEMENT REPORT", doc.internal.pageSize.width / 2, 20, { align: "center" });

  // Add generated timestamp
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated on: ${moment.utc().format("DD/MM/YYYY")}`,
    doc.internal.pageSize.width / 2,
    27,
    { align: "center" }
  );

  // Add a horizontal line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 32, doc.internal.pageSize.width - 15, 32);

  // Prepare data for the table with better column widths
  const headers = [
    { content: "Sr No", styles: { halign: 'center', cellWidth: 20 } },
    { content: "File Name", styles: { halign: 'center', cellWidth: 50 } },
    { content: "Location", styles: { halign: 'center', cellWidth: 60 } },
    // { content: "To Location", styles: { halign: 'center', cellWidth: 45 } },
    // { content: "RFID", styles: { halign: 'center', cellWidth: 50 } },
    { content: "Sender", styles: { halign: 'center', cellWidth: 50 } },
    { content: "Receiver", styles: { halign: 'center', cellWidth: 50 } }
  ];

  const tableData = data.map((row: any, index: number) => [
    { content: index + 1, styles: { halign: 'center' } },
    row.fileName || "N/A",
    row.fromLocation?.replace(/-/g, ' ') || "N/A",
    // row.toLocation?.replace(/-/g, ' ') || "N/A",
    // row.refId || "N/A",
    row.senderName || "N/A",
    row.receiverName || "N/A"
  ]);

  // Add the table using autoTable with improved styling
  autoTable(doc, {
    startY: 38,
    head: [headers.map(h => h.content)],
    body: tableData,
    theme: "grid",
    headStyles: {
      fillColor: [63, 81, 181],
      textColor: 255,
      fontStyle: "bold",
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [248, 248, 248],
    },
    styles: {
      cellPadding: 4,
      fontSize: 10,
      valign: 'middle',
      overflow: 'linebreak',
      minCellHeight: 8,
      lineColor: [220, 220, 220],
      lineWidth: 0.3
    },
    columnStyles: headers.reduce((acc: any, header, index) => {
      acc[index] = header.styles;
      return acc;
    }, {}),
    margin: { left: 15, right: 15 },
    tableWidth: 'auto'
  });

  // Add professional footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    
    // Left footer - Company info
    doc.text(
      "© 2025 MSSPL. All rights reserved.",
      15,
      doc.internal.pageSize.height - 10
    );
    
    // Center footer - Confidential notice
    doc.text(
      "CONFIDENTIAL",
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
    
    // Right footer - Page number
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width - 15,
      doc.internal.pageSize.height - 10,
      { align: "right" }
    );
  }

  // Generate PDF blob
  const pdfBlob = doc.output('blob');
  setPdfBlob(pdfBlob);

  // Set up download function
  const downloadFn = () => {
    doc.save(`File_Movement_Report_${moment().format("YYYY-MM-DD_HHmm")}.pdf`);
    setPdfModalOpen(false);
  };
  setPdfDownloadFn(() => downloadFn);

  // Open preview modal
  setPdfModalOpen(true);
};


const exportToExcel = () => {
  if (data.length === 0) {
    toast.warning(t("text.NoDataToExport"));
    return;
  }

  // Prepare the worksheet data
  const excelData = data.map((row: any, index: number) => ({
    "Sr No": index + 1,
    "File Name": row.fileName || "N/A",
    "Location": row.fromLocation?.replace(/-/g, ' ') || "N/A",
    // "To Location": row.toLocation?.replace(/-/g, ' ') || "N/A",
    // "RFID": row.refId || "N/A",
    "Sender": row.senderName || "N/A",
    "Receiver": row.receiverName || "N/A"
  }));

  // Create a new workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Calculate maximum width for each column
  const colWidths = [
    { wch: 8 },  // Sr No (fixed width)
  ];

  // Calculate dynamic widths for other columns
  const excelHeaders = ["File Name", "Location", "Sender", "Receiver"];
  excelHeaders.forEach((header, colIndex) => {
    // Start with header width
    let maxWidth = header.length * 1.3; // Character width factor
    
    // Check all data rows for this column
    excelData.forEach((row: { [x: string]: { toString: () => string; }; }) => {
      const value = row[header]?.toString() || "";
      const cellWidth = value.length * 1.1; // Slightly less than header factor
      if (cellWidth > maxWidth) {
        maxWidth = cellWidth;
      }
    });

    // Apply minimum and maximum limits
    maxWidth = Math.max(12, Math.min(maxWidth, 50)); // Min 12, Max 50
    colWidths.push({ wch: maxWidth });
  });

  // Set column widths
  ws["!cols"] = colWidths;

  // Style definitions
  const headerStyle = {
    font: {
      bold: true,
      color: { rgb: "FFFFFF" },
      sz: 12,
      name: "Calibri"
    },
    fill: {
      fgColor: { rgb: "203864" } // Navy blue
    },
    border: {
      top: { style: "medium", color: { rgb: "1F497D" } },
      bottom: { style: "medium", color: { rgb: "1F497D" } },
      left: { style: "medium", color: { rgb: "1F497D" } },
      right: { style: "medium", color: { rgb: "1F497D" } }
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
      wrapText: true
    }
  };

  const cellStyle = {
    font: {
      sz: 11,
      name: "Calibri"
    },
    border: {
      top: { style: "thin", color: { rgb: "D9D9D9" } },
      bottom: { style: "thin", color: { rgb: "D9D9D9" } },
      left: { style: "thin", color: { rgb: "D9D9D9" } },
      right: { style: "thin", color: { rgb: "D9D9D9" } }
    },
    alignment: {
      vertical: "center"
    }
  };

  // Apply styles to all cells
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1:G" + (excelData.length + 1));
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) ws[cellAddress] = {};
      
      if (R === 0) {
        // Header row
        ws[cellAddress].s = headerStyle;
      } else {
        // Data rows
        ws[cellAddress].s = {
          ...cellStyle,
          alignment: {
            ...cellStyle.alignment,
            horizontal: C === 0 ? "center" : "left" // Center align Sr No column
          }
        };
        
        // Alternate row coloring
        if (R % 2 === 0) {
          ws[cellAddress].s.fill = {
            fgColor: { rgb: "F2F2F2" } // Light gray for even rows
          };
        }
      }
    }
  }

  // Freeze header row
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomRight" };

  // Add filters to header row
  ws["!autofilter"] = { ref: XLSX.utils.encode_range(range) };

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "File Movement");

  // Add metadata
  wb.Props = {
    Title: "File Movement Report",
    Subject: "File Transfer History",
    Author: "Your Application",
    CreatedDate: new Date(),
    Company: "Your Company Name"
  };

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
    bookSST: true,
    cellStyles: true
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  });

  saveAs(blob, `File_Movement_Report_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`);
};

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleReset = () => {
    formik.resetForm({
      values: {
        hid: -1,
        fileID: 0,
        fileNumber: "",
        fileName: "",
        fromLocation: "",
        toLocation: "",
        refId: "",
        sender: "",
        receiver: "",
      },
    });
    setData([]);
    setHasSearched(false);
    setPage(0);
  };

  return (
    
    <Card
      sx={{
        width: "100%",
        boxShadow: theme.shadows[4],
        borderRadius: "12px",
        overflow: "hidden",
        marginTop: "24px",
      }}
    >
      <ToastContainer></ToastContainer>
      <Box
        sx={{
          p: 3,
          backgroundColor: "background.paper",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <DescriptionIcon
                sx={{
                  color: "primary.main",
                  fontSize: 32,
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {t("text.FileMovementReport")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
           <Grid container spacing={2} justifyContent="flex-end">
  <Grid item>
    <Button
      variant="contained"
      startIcon={<FilterAltIcon />}
      onClick={toggleFilters}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
      }}
    >
      {t("text.Filter")}
    </Button>
  </Grid>
  <Grid item>
    <Button
      variant="outlined"
      startIcon={<DescriptionIcon />}
      onClick={exportToPDF}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
        borderWidth: "2px",
        "&:hover": {
          borderWidth: "2px",
        },
      }}
    >
      {t("text.PDF")}
    </Button>
  </Grid>
  <Grid item>
    <Button
      variant="outlined"
      startIcon={<DescriptionIcon />}
      onClick={exportToExcel}
      sx={{
        fontWeight: 600,
        textTransform: "none",
        borderRadius: "6px",
        px: 3,
        py: 1,
        borderWidth: "2px",
        "&:hover": {
          borderWidth: "2px",
        },
      }}
    >
      {t("text.Excel")}
    </Button>
  </Grid>

</Grid>
          </Grid>
        </Grid>
      </Box>

      {showFilters && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "background.default",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="file-select"
                  options={optionFileDetailsAll}
                  fullWidth
                  size="small"
                  value={
                    optionFileDetailsAll.find(
                      (option) => option.value === formik.values.fileID
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("fileID", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectFile")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="rfid-select"
                  options={optionRefid}
                  fullWidth
                  size="small"
                  value={
                    optionRefid.find(
                      (option: any) => option.value === formik.values.refId
                    ) || null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue("refId", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectRFID")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid> */}

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="sender-select"
                  options={optionMember}
                  fullWidth
                  size="small"
                  value={
                    optionMember.find(
                      (option) => option.value === formik.values.sender
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("sender", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectSenderMember")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="receiver-select"
                  options={optionMember}
                  fullWidth
                  size="small"
                  value={
                    optionMember.find(
                      (option) => option.value === formik.values.receiver
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("receiver", newValue?.value || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectReceiverMember")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="from-location-select"
                  options={optionLocation}
                  fullWidth
                  size="small"
                  value={
                    optionLocation.find(
                      (option) => option.label === formik.values.fromLocation
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "fromLocation",
                      newValue?.label || ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectLocation")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              {/* <Grid item xs={12} sm={6} md={4} lg={3}>
                <Autocomplete
                  disablePortal
                  id="to-location-select"
                  options={optionDepartment}
                  fullWidth
                  size="small"
                  value={
                    optionDepartment.find(
                      (option) => option.label === formik.values.toLocation
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "toLocation",
                      newValue?.label || ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectToLocation")}
                      variant="outlined"
                    />
                  )}
                />
              </Grid> */}

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        t("text.search")
                      )}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      fullWidth
                      sx={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 600,
                        borderWidth: "2px",
                        "&:hover": {
                          borderWidth: "2px",
                        },
                      }}
                      onClick={handleReset}
                      disabled={loading}
                    >
                      {t("text.Clear")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}

      <Box sx={{ p: 3 }}>
        <TableContainer
          component={Paper}
          id="tabcont"
          sx={{
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
            overflow: "auto",
            maxHeight: "calc(100vh - 300px)",
          }}
        >
          <Table
            aria-label="file movement table"
            stickyHeader
            sx={{
              minWidth: 650,
              "& .MuiTableCell-root": {
                padding: "12px 16px",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">
                  {t("text.SrNo")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.FileName")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.Location")}
                </StyledTableCell>
                {/* <StyledTableCell align="center">
                  {t("text.ToLocation")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.RFID")}
                </StyledTableCell> */}
                <StyledTableCell align="center">
                  {t("text.Sender")}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {t("text.Receiver")}
                </StyledTableCell>
                 <StyledTableCell align="center">
                  {t("text.PreviewFile")}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                (rowsPerPage > 0
                  ? data.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : data
                ).map((row: any, index: any) => (
                  <Row
                    key={index}
                    row={row}
                    index={page * rowsPerPage + index}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {hasSearched
                       ? t("text.Nodatafoundfortheselectedfilters")
                        : t("text.Pleaseapplyfiltersandclicksearchtoviewdata")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 0,
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
            colSpan={3}
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                "aria-label": "rows per page",
                
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
            sx={{
              border: "none",
              "& .MuiTablePagination-toolbar": {
                padding: 0,
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
              },
            }}
          />
        </Paper>
      </Box>

      <iframe
        id="ifmcontentstoprint"
        style={{ height: "0px", width: "0px", position: "absolute" }}
      />
      <PDFPreviewModal
  open={pdfModalOpen}
  onClose={() => setPdfModalOpen(false)}
  pdfBlob={pdfBlob}
  onDownload={pdfDownloadFn || (() => {})}
/>
    </Card>
  );
}



// import React, { useState, useEffect } from "react";
// import {
//   Button,
//   Card,
//   Divider,
//   Paper,
//   Grid,
//   SwipeableDrawer,
//   Typography,
//   TextField,
// } from "@mui/material";
// import FilterAltIcon from "@mui/icons-material/FilterAlt";
// import Box from "@mui/material/Box";
// import Collapse from "@mui/material/Collapse";
// import IconButton from "@mui/material/IconButton";
// import Table from "@mui/material/Table";
// import TableBody from "@mui/material/TableBody";
// import TableCell, { tableCellClasses } from "@mui/material/TableCell";
// import TableContainer from "@mui/material/TableContainer";
// import TableHead from "@mui/material/TableHead";
// import TableRow from "@mui/material/TableRow";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
// import axios from "axios";
// import HOST_URL from "../../../utils/Url";
// import ToastApp from "../../../ToastApp";
// import { toast } from "react-toastify";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import Autocomplete from "@mui/material/Autocomplete";
// import { useTranslation } from "react-i18next";
// import PrintIcon from "@mui/icons-material/Print";
// import dayjs from "dayjs";
// import { styled } from "@mui/material/styles";
// import api from "../../../utils/Url";
// import { Label } from "@mui/icons-material";

// const StyledTableCell = styled(TableCell)(({ theme }) => ({
//   [`&.${tableCellClasses.head}`]: {
//     backgroundColor: "#00009C",
//     color: theme.palette.common.white,
//   },
//   [`&.${tableCellClasses.body}`]: {
//     fontSize: 14,
//   },
// }));

// const StyledTableRow = styled(TableRow)(({ theme }) => ({
//   "&:nth-of-type(odd)": {
//     backgroundColor: theme.palette.action.hover,
//   },
//   // hide last border
//   "&:last-child td, &:last-child th": {
//     border: 0,
//   },
// }));

// function Row({ row, index }: { row: any; index: number }) {

//   return (
//     <React.Fragment>
//       <StyledTableRow sx={{ border: "1px gray solid", padding: "5px" }}>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {index + 1}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }}>
//           {row.fileNumber}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.fromLocation}
//         </TableCell>
//         {/* <TableCell style={{ border: "1px gray solid" }}  align="center">
//             {row.synopsis}
//           </TableCell> */}
//         <TableCell style={{ border: "1px gray solid" }} >
//           {row.toLocation}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.refId}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.senderName}
//         </TableCell>
//         <TableCell style={{ border: "1px gray solid" }} align="center">
//           {row.receiverName}
//         </TableCell>
//       </StyledTableRow>
//     </React.Fragment>
//   );
// }

// export default function FileMovementReports() {
  

//   const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
//   const [data, setData] = useState<any>([]);
//   const { t } = useTranslation();


//   const [optionLocation, setOptionLocation] = useState([
//     { value: "-1", label: t("text.SelectFromLocation") },
//   ]);

//   const [optionMember, setOptionMember] = useState([
//     { value: "-1", label: t("text.SelectSenderMember")},
//   ]);
//   const [optionFileDetailsAll, setOptionFileDetailsAll] = useState([
//     { value: "-1", label: t("text.SelectFile") },
//   ]);
//   const [optionRefid, setOptionRefid] = useState([
//     { label: t("text.SelectRFID") },
//   ]);
//   const [optionDepartment, setOptionDepartment] = useState([
//     {value:"-1", label:"Select Department"}
//   ])

//   useEffect(() => {
//     getLocationData();
//     getFileTypeData();
//     getFileDtailsAllData();
//     getDepartmentData();
//   }, []);

//   const getDepartmentData = () => {
//     const collectData = {
//       departmentId: -1,
//     };

//     api
//       .post("Department/GetDepartmentmaster", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["departmentName"],
//             value: res.data.data[index]["departmentId"],
//           });
//         }
//         setOptionDepartment(arr);
//       });
//   };



//   const getLocationData = () => {
//     const collectData = {
//       id: -1,
//     };

//     api
//       .post("LocationItems/GetMappedLocation", collectData)
//       .then((res) => {
//         const arr = [];
//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label: res.data.data[index]["location"],
//             value: res.data.data[index]["id"],
//           });
//         }
//         setOptionLocation(arr);
//       });
//   };


//   const getFileTypeData = () => {
//     const collectData = {
//       useR_ID: "-1",
//     };
//     api.post(`USER/GetUSER`, collectData).then((res) => {
//       const arr = res.data.data.map((item: any) => ({
//         label: item.firsT_NAME  + " " + item.middlE_NAME + "  "+ item.suR_NAME,
//         value: item.useR_ID,
//       }));
//         setOptionMember(arr);
//       });
//   };

//   const getFileDtailsAllData = () => {
//     const collectData =
//     {
//       "fileID": -1,
//       "chackNo": "",
//       "fileType": -1,
//       "fileGroup": -1,
//       "fileCategory": -1,
//       "fileClass": -1,
//       "fileSubject": -1,
//       "show": true,
//       "exportOption": "string"
//     }

//     api
//       .post( "FileDetail/GetFileDetails", collectData)
//       .then((res) => {
//         const arr = [];
//         const arr1: any = [];

//         console.log("result" + JSON.stringify(res.data));
//         for (let index = 0; index < res.data.data.length; index++) {
//           arr.push({
//             label:res.data.data[index]["fileNo"]  + " - " + res.data.data[index]["fileName"]  ,
//             value: res.data.data[index]["fileID"],
//           });
//         }

//         for (let index = 0; index < res.data.data.length; index++) {
//           const rfid = res.data.data[index]["rfid"];
//           const refID = res.data.data[index]["fileID"];

//           // Check if rfid is not empty or zero
//           if (rfid !== "" && rfid !== 0) {
//             arr1.push({
//               label: rfid,
//               value: refID,
//             });
//           }
//         }
//         setOptionRefid(arr1);
//         console.log("arr1", arr1);
//         setOptionFileDetailsAll(arr);
//       });
//   };

//   const handleFilterButtonClick = () => {
//     setFilterDrawerOpen(true);
//     console.log("value handleFilterButtonClick", filterDrawerOpen);
//   };

//   const handleCloseDrawer = () => {
//     setFilterDrawerOpen(false);
//   };

//   interface FormValues {
//     fileID: any;
//     fileNumber: any;
//     hid:any;
//     fromLocation: any;
//     toLocation: any;
//     refId: any;
//     sender: any;
//     receiver: any;
//   }

//   const initialValues: FormValues = {
//     "hid": -1,
//   "fileID": 0,
//   "fileNumber": "",
//   "fromLocation": "",
//   "toLocation": "",
//   "refId": 0,
//   "sender": "",
//   "receiver": ""
//   };

//   const formik = useFormik({
//     initialValues: initialValues,

//     onSubmit: async (values) => {
//       // console.log("Form values:", formik.values);

//       if (values.fileID === initialValues.fileID) {
//         values.fileID = -1;
//       }
//       if (values.fileNumber === initialValues.fileNumber) {
//         values.fileNumber = "";
//       }
//       if (values.fromLocation === initialValues.fromLocation) {
//         values.fromLocation = "";
//       }
//       if (values.toLocation === initialValues.toLocation) {
//         values.toLocation = "";
//       }
//       if (values.refId === initialValues.refId) {
//         values.refId = -1;
//       }
//       if (values.sender === initialValues.sender) {
//         values.sender = "";
//       }
//       if (values.receiver === initialValues.receiver) {
//         values.receiver = "";
//       }
     

//       console.log("check data before submitting", values);

//       const response = await api.post(
//         `FileMovementHistory/GetFileMovementHistoryFilter`,
//         values
//       );
//       setData(response.data.data);
//       handleCloseDrawer();
//       if (response.data.isSuccess) {
//         toast.success(response.data.mesg);
//       } else {
//         toast.error(response.data.mesg);
//       }
//     },
//   });

//   const handlePrint = () => {
//     const contentElement = document.getElementById("tabcont");
//     if (contentElement) {
//       const clonedContent = contentElement.cloneNode(true) as HTMLElement;

//       const filterButton = clonedContent.querySelector(".filter-button");
//       if (filterButton) {
//         filterButton.remove();
//       }
//       const printButton = clonedContent.querySelector(".print-button");
//       if (printButton) {
//         printButton.remove();
//       }

//       const printWindow = window.open("", "_blank");
//       if (printWindow) {
//         printWindow.document.body.appendChild(clonedContent);

//         printWindow.print();
//       } else {
//         console.error("Error: Unable to open print window.");
//       }
//     }
//   };

//   return (
//     <Card
//       style={{
//         width: "100%",
//         backgroundColor: "#E9FDEE",
//         border: ".5px solid #fff ",
//         marginTop: "3vh",
//       }}
//     >
//       <Paper
//         sx={{
//           width: "100%",
//           overflow: "hidden",
//           "& .MuiDataGrid-colCell": {
//             backgroundColor: "#f0ad4e",
//             color: "#000",
//             fontSize: 17,
//             fontWeight: 900,
//           },
//         }}
//         style={{ padding: "10px" }}
//       >
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//           }}
//         >
//           <Typography
//             gutterBottom
//             variant="h5"
//             component="div"
//             sx={{ padding: "20px" }}
//             align="left"
//           >
//            {t("text.FileMovementReport")}
//           </Typography>

//           <div
//             style={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               gap: 20,
//             }}
//           >
//             <Button
//               type="button"
//               onClick={() => handleFilterButtonClick()}
//               style={{
//                 backgroundColor: "blue",
//                 fontSize: "15px",
//                 color: "#fff",
//                 textTransform: "none",
//                 cursor: "pointer",
//               }}
//             >
//               <FilterAltIcon />
//               {t("text.Filter")}
//             </Button>
//             <Button
//               type="button"
//               onClick={handlePrint}
//               style={{
//                 backgroundColor: "blue",
//                 fontSize: "15px",
//                 color: "#fff",
//                 textTransform: "none",
//                 cursor: "pointer",
//               }}
//             >
//               <PrintIcon />
//               {t("text.Print")}
//             </Button>
//             <iframe
//               id="ifmcontentstoprint"
//               style={{ height: "0px", width: "0px", position: "absolute" }}
//             ></iframe>
//           </div>
//           <SwipeableDrawer
//             anchor="right"
//             open={filterDrawerOpen}
//             onClose={handleCloseDrawer}
//             onOpen={() => {}}
//             slotProps={{
//               backdrop: {
//                 style: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
//               },
//             }}
//             PaperProps={{
//               style: {
//                 width: 310,
//                 boxShadow: "0px 0px 8px 0px rgba(0,0,0,0.2)",
//               },
//             }}
//             style={{
//               zIndex: 1300,
//             }}
//           >
//             <Typography
//               gutterBottom
//               variant="h5"
//               component="div"
//               sx={{
//                 margin: "20px 0px",
//               }}
//               align="center"
//             >
//                {t("text.FileMovementReport")}
//             </Typography>
//             <Divider />

//             <div style={{ margin: "20px 0px" }}>
//               <form onSubmit={formik.handleSubmit}>
//                 <Grid
//                   container
//                   spacing={2}
//                   sx={{
//                     display: "flex",
//                     flexDirection: "row",
//                     gap: 2,
//                     marginLeft: "0px",
//                   }}
//                 >

// <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionFileDetailsAll}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);

//                         formik.setFieldValue("fileID", newValue?.value);
//                         formik.setFieldTouched("fileID", true);
//                         formik.setFieldTouched("fileID", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectFile")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionRefid}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue:any) => {
//                         console.log(newValue);
//                         // fileID
//                         formik.setFieldValue("fileID", newValue?.value);
//                         //formik.setFieldTouched("refId", true);
//                         //formik.setFieldTouched("refId", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectRFID")} />
//                       )}
//                     />
//                   </Grid>

//                    <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionMember}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);
//                         formik.setFieldValue("sender", newValue?.value);
//                         formik.setFieldTouched("sender", true);
//                         formik.setFieldTouched("sender", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectSenderMember")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionMember}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);
//                         formik.setFieldValue("receiver", newValue?.value);
//                         formik.setFieldTouched("receiver", true);
//                         formik.setFieldTouched("receiver", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectReceiverMember")} />
//                       )}
//                     />
//                   </Grid> 

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionLocation}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);

//                         formik.setFieldValue("fromLocation", newValue?.label?.toString());
//                         formik.setFieldTouched("fromLocation", true);
//                         formik.setFieldTouched("fromLocation", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectFromLocation")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={11} sm={11} item>
//                     <Autocomplete
//                       disablePortal
//                       id="combo-box-demo"
//                       options={optionDepartment}
//                       fullWidth
//                       size="small"
//                       onChange={(event, newValue) => {
//                         console.log(newValue?.value);

//                         formik.setFieldValue("toLocation", newValue?.label?.toString());
//                         formik.setFieldTouched("toLocation", true);
//                         formik.setFieldTouched("toLocation", false);
//                       }}
//                       renderInput={(params) => (
//                         <TextField {...params} label={t("text.SelectToLocation")} />
//                       )}
//                     />
//                   </Grid>

//                   <Grid xs={10} item>
//                     <div style={{ justifyContent: "space-between", flex: 1 }}>
//                       <Button
//                         type="submit"
//                         variant="contained"
//                         style={{
//                           width: "37%",
//                           // backgroundColor: "#059669",
//                           margin: "0.5%",
//                         }}
//                       >
//                        {t("text.search")}
//                       </Button>
//                       <Button
//                         type="reset"
//                         variant="contained"
//                         style={{
//                           width: "37%",
//                           // backgroundColor: "#F43F5E",
//                           margin: "0.5%",
//                         }}
//                         onClick={() => {
//                           formik.resetForm();

//                         }}
//                       >
//                         {t("text.Clear")}
//                       </Button>
//                     </div>
//                   </Grid>
//                 </Grid>
//               </form>
//             </div>
//           </SwipeableDrawer>
//         </div>

//         <Divider />
//         <br />

//         <TableContainer
//           component={Paper}
//           id="tabcont"
//           sx={{
//             maxHeight: "65vh",
//             marginBottom: "10px",
//             border: "1px solid #fff",
//           }}
//         >
//           <Table
//             aria-label="customized  table"
//             style={{
//               border: "1px gray solid",
//               borderCollapse: "collapse",
//               width: "100%",
//             }}
//           >
//             <TableHead
//                style={{
//                 border: "1px gray solid",
//                 borderCollapse: "collapse",
//                 position: "sticky",
//                 top: 0, 
//                 zIndex: 1, 
//                 backgroundColor: "#fff",
//               }}
//             >
//               <StyledTableCell
//                 colSpan={10}
//                 align="center"
//                 style={{
//                   fontSize: 17,
//                   fontWeight: 600,
//                   border: "1px gray solid",
//                   padding: "5px",
//                   backgroundColor: "transparent",
//                   color: "#000",
//                 }}
//               >
//                  {t("text.FileMovementReport")}
//               </StyledTableCell>
//               <TableRow>
//                 {/* <StyledTableCell /> */}
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.SrNo")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.FileNo")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.FromLocation")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.ToLocation")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                  {t("text.RFID")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.Sender")}
//                 </StyledTableCell>
//                 <StyledTableCell
//                   align="center"
//                   style={{
//                     fontSize: 17,
//                     fontWeight: 500,
//                     border: "1px gray solid",
//                     padding: "10px",
//                   }}
//                 >
//                   {t("text.Receiver")}
//                 </StyledTableCell>
//               </TableRow>
//             </TableHead>
//            {data && <TableBody>
//               {data.map((row: any, index: any) => (
//                 <Row key={row.any} row={row} index={index} />
//               ))}
//             </TableBody>}
//           </Table>
//         </TableContainer>
//       </Paper>
//     </Card>
//   );
// }
